import { Observable, ReplaySubject, distinctUntilChanged, filter, map } from 'rxjs';
import { BusEvents } from './bus-events.enum';

interface EventPayload<T = any> {
  event: BusEvents;
  data?: T;
}

class AgrEventBusService<T = any> {
  private readonly eventSubject = new ReplaySubject<EventPayload<T>>(1);
  private readonly debug = false;

  /**
   * Emits a new event with optional data.
   * @param event - The name of the event (BusEvents enum).
   * @param data - Optional data associated with the event.
   */
  emit<K extends keyof T>(event: K, data?: T[K]): void {
    if (!event) {
      console.error('Invalid event:', event);
      return;
    }
    if (this.debug) {
      console.log('EVENT EMITTED:', event, data);
    }
    this.eventSubject.next({ event: event as BusEvents, data: data as T });
  }

  /**
   * Listens for the specified event and returns an observable that will emit data
   * whenever the event is triggered.
   *
   * @param event - The name of the event to subscribe to.
   * @returns  The observable that emits the event data.
   */
  on<K extends keyof T>(event: K): Observable<T[K] | undefined> {
    return this.eventSubject.asObservable().pipe(
      filter(e => e.event === event),
      map(e => e.data as T[K]),
      distinctUntilChanged()
    );
  }
}

export const eventBus = new AgrEventBusService();
